import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { LogOut, User, X, RefreshCw, ToggleLeft, ToggleRight, Truck, AlertTriangle, Flag, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import confetti from "canvas-confetti";
import LoginPage from "./LoginPage";
import CommutesPanel from "./CommutesPanel";
import FuelHud from "./FuelHud";
import MapWidget, { RouteStationsPanel } from "./MapWidget";
import CalculatorInputs from "./CalculatorInputs";
import HistoryPanel from "./HistoryPanel";
import TrendsChart from "./TrendsChart";
import VehicleSelector from "./VehicleSelector";
import SplitShare from "./SplitShare";
import CommunityStats from "./CommunityStats";
import AdminPage from "./AdminPage";
import PrivacyPolicy from "./PrivacyPolicy";
import ShareCard from "./ShareCard";
import MileageReimbursement from "./MileageReimbursement";
import MileageHistoryPanel from "./MileageHistoryPanel";
import { API, FUEL_TYPES, geocodeAddress, getPlaceDetails, reverseGeocode, searchPlaces, calculateOSRMRoute, formatRouteInfo, formatCurrency, detectTolls, detectTowingWarnings, detectFerry, calculateRUC, FERRY_DEFAULT_COST, RUC_RATE_PER_1000KM, RUC_WEIGHT_BANDS, authConfig } from "./constants";

// Mapbox marker element helpers
const createMarkerEl = (color) => {
  const el = document.createElement('div');
  el.style.cssText = `width:28px;height:28px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;cursor:pointer`;
  const inner = document.createElement('div');
  inner.style.cssText = 'width:8px;height:8px;border-radius:50%;background:#fff';
  el.appendChild(inner);
  return el;
};
const createMarker = (type) => new mapboxgl.Marker({ element: createMarkerEl(type === 'origin' ? '#16A34A' : '#DC2626') });

function App() {
  // === ADMIN PAGE ===
  const [showAdmin, setShowAdmin] = useState(() => new URLSearchParams(window.location.search).has('admin'));
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  // Auto-dismiss splash after 2.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  // === PULL TO REFRESH ===
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const pulling = useRef(false);
  const PULL_THRESHOLD = 80;

  useEffect(() => {
    const onTouchStart = (e) => {
      if (window.scrollY === 0 && !refreshing) {
        touchStartY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };
    const onTouchMove = (e) => {
      if (!pulling.current || refreshing) return;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (dy > 0 && window.scrollY === 0) {
        setPullDistance(Math.min(dy * 0.5, 120));
      } else {
        pulling.current = false;
        setPullDistance(0);
      }
    };
    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistance >= PULL_THRESHOLD) {
        setRefreshing(true);
        setPullDistance(PULL_THRESHOLD);
        // Refresh fuel prices + history
        Promise.all([
          axios.get(`${API}/gaspy/prices`).then(r => { if (r.data) setFuelPrices(r.data); }).catch(() => {}),
          fetchHistory(),
        ]).finally(() => {
          setRefreshing(false);
          setPullDistance(0);
          toast.success("Refreshed!");
        });
      } else {
        setPullDistance(0);
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchHistory is stable (useCallback []), other refs are module-level
  }, [pullDistance, refreshing]);

  // === AUTH STATE ===
  const [user, setUser] = useState(undefined);
  const [showLogin, setShowLogin] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const authChecked = useRef(false);

  // === FUEL PRICE STATE ===
  const [fuelPrices, setFuelPrices] = useState(null);
  const [selectedFuelType, setSelectedFuelType] = useState("diesel");
  const [priceSource, setPriceSource] = useState("");
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [showFuelDropdown, setShowFuelDropdown] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("national");
  const [regionalPrices, setRegionalPrices] = useState(null);
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");
  const [showPriceOverride, setShowPriceOverride] = useState(false);

  // Save custom price when user changes it (debounced)
  useEffect(() => {
    if (user === undefined || !user) return;
    const timer = setTimeout(() => {
      axios.put(`${API}/user/preferences`, { custom_price: priceOverride || null }, authConfig()).catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
  }, [priceOverride]);

  // Load saved custom price on login
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/preferences`, authConfig())
      .then(r => {
        if (r.data.custom_price) {
          setPriceOverride(String(r.data.custom_price));
          setShowPriceOverride(true);
        }
      }).catch(() => {});
  }, [user?.user_id]);

  // === PWA INSTALL ===
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  useEffect(() => {
    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) return;
    // Detect iOS Safari
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|Chrome/.test(ua);
    if (isIOS && isSafari) {
      const dismissed = localStorage.getItem("drivespend_pwa_dismissed");
      if (!dismissed) { setIsIOSSafari(true); setShowInstallBanner(true); }
      return;
    }
    // Chrome/Android install prompt
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  const handleInstall = async () => {
    if (isIOSSafari) {
      // Can't programmatically install on iOS — just dismiss the banner
      localStorage.setItem("drivespend_pwa_dismissed", "1");
      setShowInstallBanner(false);
      return;
    }
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") setShowInstallBanner(false);
    setInstallPrompt(null);
  };
  const dismissInstallBanner = () => {
    localStorage.setItem("drivespend_pwa_dismissed", "1");
    setShowInstallBanner(false);
  };

  // === CALCULATOR STATE ===
  const [distance, setDistance] = useState("100");
  const [consumption, setConsumption] = useState(() => localStorage.getItem("fuelConsumption") || "8");
  const handleConsumptionChange = (value) => {
    setConsumption(value);
    if (value && !isNaN(parseFloat(value)) && parseFloat(value) > 0) localStorage.setItem("fuelConsumption", value);
  };
  const [liveResult, setLiveResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResultAnimation, setShowResultAnimation] = useState(false);
  const [returnTrip, setReturnTrip] = useState(false);
  const [tripName, setTripName] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // === EXTRA COSTS STATE ===
  const [rucEnabled, setRucEnabled] = useState(true);
  const [rucTier, setRucTier] = useState("light");
  const [detectedTolls, setDetectedTolls] = useState([]);
  const [tollOverride, setTollOverride] = useState("");
  const [ferryDetected, setFerryDetected] = useState(false);
  const [ferryCost, setFerryCost] = useState(FERRY_DEFAULT_COST.toString());

  // Auto-apply RUC tier from selected vehicle
  useEffect(() => {
    if (selectedVehicle?.ruc_tier) setRucTier(selectedVehicle.ruc_tier);
  }, [selectedVehicle?.id, selectedVehicle?.ruc_tier]);

  // === TOWING STATE ===
  const [towingEnabled, setTowingEnabled] = useState(false);
  const [towingWarnings, setTowingWarnings] = useState([]);

  // === HISTORY STATE ===
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // === MAP STATE ===
  const [originInput, setOriginInput] = useState("");
  const [destinationInput, setDestinationInput] = useState("");
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeStations, setRouteStations] = useState(null);
  const [fuelStations, setFuelStations] = useState(null);
  const [loadingStations, setLoadingStations] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [selectingMode, setSelectingMode] = useState("origin");
  const [waypoints, setWaypoints] = useState([]);
  const [waypointInput, setWaypointInput] = useState("");
  const [showWaypointInput, setShowWaypointInput] = useState(false);
  const waypointInputRef = useRef(null);
  const [commuteFromRoute, setCommuteFromRoute] = useState(null);
  const [altRouteInfo, setAltRouteInfo] = useState(null);
  const [allRoutes, setAllRoutes] = useState(null);
  const [activeRouteIndex, setActiveRouteIndex] = useState(0);

  // === AUTOCOMPLETE STATE ===
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const originSearchTimeout = useRef(null);
  const destSearchTimeout = useRef(null);

  // === MAPBOX REFS ===
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const mapInitialized = useRef(false);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const originCoordsRef = useRef(null);
  const destinationCoordsRef = useRef(null);
  const selectingModeRef = useRef("origin");

  // === POI STATE ===
  const [poiEnabled, setPoiEnabled] = useState(false);
  const [poiTypes, setPoiTypes] = useState(["campsite", "nzmca", "caravan", "viewpoint", "fuel"]);
  const poiMarkersRef = useRef([]);
  const fuelStationMarkersRef = useRef([]);
  const [addingCustomPin, setAddingCustomPin] = useState(false);
  const addingCustomPinRef = useRef(false);

  // === POI SUBMISSION MODAL STATE ===
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinCoords, setPinCoords] = useState(null);
  const [pinName, setPinName] = useState("");
  const [pinType, setPinType] = useState("campsite");
  const [pinDesc, setPinDesc] = useState("");
  const [pinSubmitting, setPinSubmitting] = useState(false);

  const PIN_TYPES = [
    { key: "campsite", label: "Camping Site" },
    { key: "scenic", label: "Scenic Spot" },
    { key: "hiking", label: "Walking Track" },
    { key: "cycling", label: "Cycle Track" },
    { key: "toilets", label: "Public Toilet" },
    { key: "water", label: "Water Fill" },
    { key: "dump_station", label: "Dump Station" },
    { key: "free_parking", label: "Free Parking" },
    { key: "nzmca", label: "NZMCA" },
    { key: "caravan", label: "Caravan Park" },
    { key: "viewpoint", label: "Lookout" },
    { key: "rest_area", label: "Rest Stop" },
    { key: "towing_restriction", label: "Towing Restriction" },
  ];

  const handlePinSubmit = async () => {
    if (!pinName.trim() || !pinCoords) return;
    setPinSubmitting(true);
    try {
      const resp = await axios.post(`${API}/pois/custom`, { name: pinName.trim(), type: pinType, lat: pinCoords.lat, lon: pinCoords.lng, description: pinDesc.trim() }, authConfig());
      toast.success(resp.data.message || "Submitted for review!");
      setShowPinModal(false); setPinName(""); setPinDesc(""); setPinCoords(null);
      fetchAndDisplayPois();
    } catch { toast.error("Login required to submit locations"); }
    finally { setPinSubmitting(false); }
  };

  const handleReportRestriction = () => {
    if (!user) { toast.error("Please sign in to report a restriction"); return; }
    setPinType("towing_restriction");
    setPinName("");
    setPinDesc("");
    setPinCoords(null);
    setShowPinModal(true);
    toast("Tap the map to place the restriction, or enter coordinates manually");
    setAddingCustomPin(true);
    addingCustomPinRef.current = true;
  };

  // === SHARE STATE ===
  const [shareCopied, setShareCopied] = useState(false);
  const [sharedTrip, setSharedTrip] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);
  const [mileageRefreshKey, setMileageRefreshKey] = useState(0);

  useEffect(() => { selectingModeRef.current = selectingMode; }, [selectingMode]);

  // === COMPUTED VALUES ===
  const gaspyFuelPrice = fuelPrices ? fuelPrices[selectedFuelType] : null;
  const regionOffset = regionalPrices?.find(r => r.region_key === selectedRegion);
  const currentFuelPrice = priceOverride && parseFloat(priceOverride) > 0
    ? parseFloat(priceOverride)
    : (regionOffset ? regionOffset[selectedFuelType === "unleaded_91" ? "unleaded_91" : selectedFuelType === "unleaded_95" ? "unleaded_95" : "diesel"] : gaspyFuelPrice) || 0;

  // === AUTH (httpOnly cookie-based) ===
  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    const processAuth = async () => {
      // Google OAuth: Emergent returns ?session_id=... in the URL hash on redirect.
      // Exchange it for an app cookie via POST /api/auth/google.
      const hash = window.location.hash;
      if (hash.includes("session_id=")) {
        const sessionId = hash.split("session_id=")[1]?.split("&")[0];
        window.location.hash = "";
        if (sessionId) {
          try {
            const resp = await axios.post(`${API}/auth/google`, { session_id: sessionId });
            setUser(resp.data);
            return;
          } catch (err) { console.warn("Google session exchange failed:", err); }
        }
      }
      // Cookie-based session: ask the server who we are. Cookie auto-attaches via axios.defaults.withCredentials.
      try {
        const resp = await axios.get(`${API}/auth/me`);
        setUser(resp.data);
      } catch {
        setUser(null);
      }
      // One-shot legacy cleanup: remove any old Bearer token from localStorage now that auth is cookie-based.
      try { localStorage.removeItem("drivespend_token"); } catch (_) { /* private-mode storage may throw */ }
    };
    processAuth();
  }, []);

  // Record anonymous page view once auth state is known
  useEffect(() => {
    if (user === undefined) return; // still loading
    axios.post(`${API}/analytics/event`, {
      event: "page_view",
      logged_in: !!user,
      platform: window.matchMedia("(display-mode: standalone)").matches ? "pwa" : "web",
    }).catch(() => {});
  }, [user === undefined ? undefined : !!user]); // fire once auth resolves, and on login/logout

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (err) { console.warn("Logout request failed:", err); }
    try { localStorage.removeItem("drivespend_token"); } catch (_) { /* ignore */ }
    setUser(null);
  };
  const handleLogin = (userData) => {
    // Cookie was set by the server response. Nothing to store client-side.
    setUser(userData || null);
    setShowLogin(false);
    // Map was destroyed when LoginPage replaced the DOM — allow re-init
    mapInitialized.current = false;
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`);
      toast.success("Account permanently deleted");
      try { localStorage.removeItem("drivespend_token"); } catch (_) { /* ignore */ }
      setShowDeleteConfirm(false);
      setUser(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete account. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // === SHARED TRIP URL PARAMS ===
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("share")) {
      const shared = {
        distance: params.get("d") || "", consumption: params.get("c") || "",
        fuelType: params.get("ft") || "diesel", price: params.get("p") || "",
        returnTrip: params.get("rt") === "1", tripName: params.get("tn") || "",
        origin: params.get("or") || "", destination: params.get("de") || "",
      };
      if (shared.distance) setDistance(shared.distance);
      if (shared.consumption) setConsumption(shared.consumption);
      if (shared.fuelType) setSelectedFuelType(shared.fuelType);
      if (shared.price) { setPriceOverride(shared.price); setShowPriceOverride(true); }
      if (shared.returnTrip) setReturnTrip(true);
      if (shared.tripName) setTripName(shared.tripName);
      if (shared.origin) setOriginInput(shared.origin);
      if (shared.destination) setDestinationInput(shared.destination);
      setSharedTrip(shared);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const generateShareLink = () => {
    const params = new URLSearchParams();
    params.set("share", "1");
    if (distance) params.set("d", distance);
    if (consumption) params.set("c", consumption);
    params.set("ft", selectedFuelType);
    if (currentFuelPrice) params.set("p", currentFuelPrice.toString());
    if (returnTrip) params.set("rt", "1");
    if (tripName) params.set("tn", tripName);
    if (originInput) params.set("or", originInput);
    if (destinationInput) params.set("de", destinationInput);
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  };

  const handleShareTrip = async () => {
    const url = generateShareLink();
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true); toast.success("Share link copied to clipboard!");
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      const input = document.createElement("input");
      input.value = url; document.body.appendChild(input); input.select();
      document.execCommand("copy"); document.body.removeChild(input);
      setShareCopied(true); toast.success("Share link copied!");
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const handleExportCSV = async () => {
    try {
      const resp = await fetch(`${API}/history/export/csv`, { credentials: "include" });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `drivespend_history_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url); toast.success("History exported as CSV");
    } catch { toast.error("Failed to export history"); }
  };

  // Helper to remove route layers from map
  const removeRouteLayers = (map) => {
    ['route-main', ...Array.from({length: 5}, (_, i) => `route-alt-${i}`)].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    });
  };

  // === MAPBOX MAP INITIALIZATION ===
  useEffect(() => {
    if (showSplash || showLogin || mapInitialized.current || !mapContainerRef.current) return;
    mapInitialized.current = true;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [174.886, -40.9006],
      zoom: 5,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Place label layer IDs for interactivity
    const PLACE_LAYERS = [];
    let placeHoverPopup = null;
    let clickedPlaceLayer = false;

    // Setup interactive place labels
    const setupPlaceInteractivity = () => {
      const style = map.getStyle();
      if (!style || !style.layers) return;
      style.layers.forEach(layer => {
        if (layer.type === "symbol" && /place-|settlement-|poi-label/.test(layer.id) && !PLACE_LAYERS.includes(layer.id)) {
          PLACE_LAYERS.push(layer.id);
        }
      });
      if (PLACE_LAYERS.length === 0) return;

      PLACE_LAYERS.forEach(layerId => {
        // Hover: pointer cursor + highlight popup
        map.on("mouseenter", layerId, (e) => {
          map.getCanvas().style.cursor = "pointer";
          if (e.features && e.features.length > 0) {
            const name = e.features[0].properties.name || e.features[0].properties.name_en || "";
            if (name) {
              if (placeHoverPopup) placeHoverPopup.remove();
              const mode = selectingModeRef.current;
              placeHoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: [0, -10], className: "place-hover-popup" })
                .setLngLat(e.lngLat)
                .setHTML(`<div style="font-family:'Manrope',sans-serif;font-size:12px;font-weight:700;color:#E52020;padding:2px 4px">${mode === "origin" ? "Start from" : "Go to"} ${name}</div>`)
                .addTo(map);
            }
          }
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          if (placeHoverPopup) { placeHoverPopup.remove(); placeHoverPopup = null; }
        });

        // Click: add place to route
        map.on("click", layerId, (e) => {
          if (!e.features || e.features.length === 0) return;
          clickedPlaceLayer = true;
          const feature = e.features[0];
          const name = feature.properties.name || feature.properties.name_en || "";
          if (!name) return;
          const { lng, lat } = e.lngLat;
          if (placeHoverPopup) { placeHoverPopup.remove(); placeHoverPopup = null; }
          const mode = selectingModeRef.current;
          if (mode === "origin") {
            originCoordsRef.current = { lat, lng };
            setOriginInput(name);
            if (originMarkerRef.current) originMarkerRef.current.remove();
            originMarkerRef.current = createMarker('origin').setLngLat([lng, lat]).addTo(map);
            setSelectingMode("destination");
          } else {
            destinationCoordsRef.current = { lat, lng };
            setDestinationInput(name);
            if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
            destinationMarkerRef.current = createMarker('destination').setLngLat([lng, lat]).addTo(map);
          }
        });
      });
    };

    // Register after map loads (or immediately if already loaded)
    const initPlaceLabels = () => {
      setupPlaceInteractivity();
      window._mapboxMap = map;
    };
    if (map.loaded()) {
      initPlaceLabels();
    } else {
      map.on("load", initPlaceLabels);
    }
    // Also handle style reload
    map.on("style.load", setupPlaceInteractivity);

    // General click handler for map (fallback for non-label areas)
    map.on("click", async (e) => {
      // Skip if a place label was clicked (handled above)
      if (clickedPlaceLayer) { clickedPlaceLayer = false; return; }
      const { lng, lat } = e.lngLat;

      // Custom pin mode: open submission modal
      if (addingCustomPinRef.current) {
        addingCustomPinRef.current = false;
        setAddingCustomPin(false);
        setPinCoords({ lat, lng });
        setShowPinModal(true);
        return;
      }

      const address = await reverseGeocode(lat, lng);
      const mode = selectingModeRef.current;
      if (mode === "origin") {
        originCoordsRef.current = { lat, lng };
        setOriginInput(address);
        if (originMarkerRef.current) originMarkerRef.current.remove();
        originMarkerRef.current = createMarker('origin').setLngLat([lng, lat]).addTo(map);
        setSelectingMode("destination");
      } else {
        destinationCoordsRef.current = { lat, lng };
        setDestinationInput(address);
        if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
        destinationMarkerRef.current = createMarker('destination').setLngLat([lng, lat]).addTo(map);
      }
    });

    // Geolocation auto-start
    if (navigator.geolocation) {
      const autoLocateSuccess = async (pos) => {
        const { latitude, longitude } = pos.coords;
        originCoordsRef.current = { lat: latitude, lng: longitude };
        const address = await reverseGeocode(latitude, longitude);
        setOriginInput(address);
        if (originMarkerRef.current) originMarkerRef.current.remove();
        originMarkerRef.current = createMarker('origin').setLngLat([longitude, latitude]).addTo(map);
        setSelectingMode("destination");
        map.flyTo({ center: [longitude, latitude], zoom: 10 });
      };
      const autoLocateError = (err) => {
        if (err.code === 3) {
          navigator.geolocation.getCurrentPosition(autoLocateSuccess, () => {}, { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
        }
      };
      navigator.geolocation.getCurrentPosition(autoLocateSuccess, autoLocateError, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }

    return () => map.remove();
  }, [showSplash, showLogin]);

  // === POI LAYER ===
  const poiFetchTimeout = useRef(null);
  const clearPoiMarkers = () => {
    poiMarkersRef.current.forEach(m => m.remove());
    poiMarkersRef.current = [];
  };

  const fetchAndDisplayPois = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !poiEnabled) return;
    const zoom = map.getZoom();
    if (zoom < 9) { clearPoiMarkers(); return; }
    const bounds = map.getBounds();
    const south = bounds.getSouth();
    const west = bounds.getWest();
    const north = bounds.getNorth();
    const east = bounds.getEast();
    try {
      const resp = await axios.get(`${API}/pois`, {
        params: { south, west, north, east, types: poiTypes.join(",") },
      });
      // Fetch custom POIs and merge
      let customPois = [];
      try {
        const customResp = await axios.get(`${API}/pois/custom`, authConfig());
        customPois = (customResp.data.pois || []).filter(p => {
          return p.lat >= south && p.lat <= north && p.lon >= west && p.lon <= east && poiTypes.includes(p.type);
        });
      } catch (err) { console.warn("Failed to fetch custom POIs:", err); }
      clearPoiMarkers();
      const poiColors = { campsite: "#16A34A", nzmca: "#E85D04", caravan: "#D97706", viewpoint: "#8B5CF6", fuel: "#F59E0B", toilets: "#666666", rest_area: "#E52020", hiking: "#16A34A" };
      const poiLabels = { campsite: "C", nzmca: "N", caravan: "CV", viewpoint: "V", fuel: "F", toilets: "T", rest_area: "R", hiking: "H" };
      const allPois = [...(resp.data.pois || []), ...customPois.map(p => ({ ...p, is_custom: true }))];
      allPois.forEach(poi => {
        const color = poiColors[poi.type] || "#666666";
        const label = poiLabels[poi.type] || "?";
        const el = document.createElement("div");
        el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${color};border:2px solid ${poi.is_custom ? '#fff' : '#fff'};box-shadow:0 1px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:11px;font-weight:700;color:#fff;font-family:'Manrope',sans-serif${poi.is_custom ? ';outline:2px dashed #E85D04;outline-offset:1px' : ''}`;
        el.textContent = label;
        const popupHtml = `<div style="font-family:'Manrope',sans-serif;padding:4px 0">
            <strong style="font-size:13px">${poi.name}</strong>
            <div style="font-size:11px;color:#666666;margin-top:2px">${poi.type.replace(/_/g, " ")}${poi.is_custom ? ' (custom pin)' : ''}</div>
            ${poi.operator ? `<div style="font-size:10px;color:#999999;margin-top:2px">${poi.operator}</div>` : ""}
            ${poi.description ? `<div style="font-size:10px;color:#475569;margin-top:4px">${poi.description.slice(0, 150)}</div>` : ""}
            ${poi.website ? `<a href="${poi.website}" target="_blank" style="font-size:10px;color:#E52020;margin-top:4px;display:block">Website</a>` : ""}
          </div>`;
        const popup = new mapboxgl.Popup({ offset: 15, maxWidth: "260px" }).setHTML(popupHtml);
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([poi.lon, poi.lat]).setPopup(popup).addTo(map);
        poiMarkersRef.current.push(marker);
      });
    } catch (err) { console.error("POI fetch error:", err); }
  }, [poiEnabled, poiTypes]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!poiEnabled) { clearPoiMarkers(); return; }
    fetchAndDisplayPois();
    const onMoveEnd = () => {
      clearTimeout(poiFetchTimeout.current);
      poiFetchTimeout.current = setTimeout(fetchAndDisplayPois, 500);
    };
    map.on("moveend", onMoveEnd);
    return () => { map.off("moveend", onMoveEnd); clearTimeout(poiFetchTimeout.current); };
  }, [poiEnabled, poiTypes, fetchAndDisplayPois]);

  // === FUEL STATION MARKERS ON MAP ===
  useEffect(() => {
    const map = mapRef.current;
    fuelStationMarkersRef.current.forEach(m => m.remove());
    fuelStationMarkersRef.current = [];
    if (!map || !fuelStations?.stations?.length) return;
    fuelStations.stations.slice(0, 25).forEach((station, idx) => {
      const el = document.createElement("div");
      const isTop = idx < 3;
      el.style.cssText = `width:22px;height:22px;border-radius:50%;background:${isTop ? '#16A34A' : '#F59E0B'};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:9px;font-weight:700;color:#fff;font-family:'Manrope',sans-serif`;
      el.textContent = "$";
      const popup = new mapboxgl.Popup({ offset: 12, maxWidth: "220px" }).setHTML(
        `<div style="font-family:'Manrope',sans-serif;padding:4px 0">
          <strong style="font-size:12px">${station.name}</strong>
          <div style="font-size:11px;color:#E52020;font-weight:700;margin-top:2px">~${formatCurrency(station.estimated_price)}/L</div>
          <div style="font-size:10px;color:#666666;margin-top:2px">${station.brand ? station.brand + ' · ' : ''}${station.region}</div>
          <div style="font-size:9px;color:#999999;margin-top:1px">${station.distance_from_route_km}km from route</div>
        </div>`
      );
      const marker = new mapboxgl.Marker({ element: el }).setLngLat([station.lon, station.lat]).setPopup(popup).addTo(map);
      fuelStationMarkersRef.current.push(marker);
    });
  }, [fuelStations]);

  // === LOCATE ME ===
  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported by your browser"); return; }
    toast.info("Finding your location...");
    const onSuccess = async (pos) => {
      const { latitude, longitude } = pos.coords;
      originCoordsRef.current = { lat: latitude, lng: longitude };
      const address = await reverseGeocode(latitude, longitude);
      setOriginInput(address);
      if (originMarkerRef.current) originMarkerRef.current.remove();
      if (mapRef.current) {
        originMarkerRef.current = createMarker('origin').setLngLat([longitude, latitude]).addTo(mapRef.current);
        mapRef.current.flyTo({ center: [longitude, latitude], zoom: 12 });
      }
      setSelectingMode("destination");
      toast.success("Location found!");
    };
    const onError = (err) => {
      console.error("Geolocation error:", err.code, err.message);
      if (err.code === 1) {
        toast.error("Location access denied. Please allow location access in your browser settings and reload.");
      } else if (err.code === 2) {
        toast.error("Location unavailable. Please check your device's location settings are enabled.");
      } else if (err.code === 3) {
        navigator.geolocation.getCurrentPosition(onSuccess, () => {
          toast.error("Location timed out. Try again or enter your start location manually.");
        }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
        return;
      } else {
        toast.error("Could not get your location. Try entering your start location manually.");
      }
    };
    navigator.geolocation.getCurrentPosition(onSuccess, onError, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  };

  // === ADDRESS SEARCH ===
  const handleOriginSearch = (text) => {
    setOriginInput(text); setShowOriginSuggestions(true);
    clearTimeout(originSearchTimeout.current);
    if (text.length >= 3) {
      originSearchTimeout.current = setTimeout(async () => {
        const results = await searchPlaces(text);
        setOriginSuggestions(results);
      }, 300);
    } else setOriginSuggestions([]);
  };

  const handleDestSearch = (text) => {
    setDestinationInput(text); setShowDestSuggestions(true);
    clearTimeout(destSearchTimeout.current);
    if (text.length >= 3) {
      destSearchTimeout.current = setTimeout(async () => {
        const results = await searchPlaces(text);
        setDestSuggestions(results);
      }, 300);
    } else setDestSuggestions([]);
  };

  const selectOriginSuggestion = async (suggestion) => {
    setOriginSuggestions([]); setShowOriginSuggestions(false);
    let coords;
    if (suggestion.place_id) {
      // Google Places result — resolve coords from place_id
      setOriginInput(suggestion.display_name);
      const details = await getPlaceDetails(suggestion.place_id);
      if (!details) return;
      coords = { lat: details.lat, lng: details.lng };
    } else {
      coords = { lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) };
      setOriginInput(suggestion.display_name.split(",").slice(0, 3).join(","));
    }
    originCoordsRef.current = coords;
    if (originMarkerRef.current) originMarkerRef.current.remove();
    if (mapRef.current) {
      originMarkerRef.current = createMarker('origin').setLngLat([coords.lng, coords.lat]).addTo(mapRef.current);
      mapRef.current.flyTo({ center: [coords.lng, coords.lat], zoom: 12 });
    }
    setSelectingMode("destination");
  };

  const selectDestSuggestion = async (suggestion) => {
    setDestSuggestions([]); setShowDestSuggestions(false);
    let coords;
    if (suggestion.place_id) {
      setDestinationInput(suggestion.display_name);
      const details = await getPlaceDetails(suggestion.place_id);
      if (!details) return;
      coords = { lat: details.lat, lng: details.lng };
    } else {
      coords = { lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) };
      setDestinationInput(suggestion.display_name.split(",").slice(0, 3).join(","));
    }
    destinationCoordsRef.current = coords;
    if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
    if (mapRef.current) {
      destinationMarkerRef.current = createMarker('destination').setLngLat([coords.lng, coords.lat]).addTo(mapRef.current);
      mapRef.current.flyTo({ center: [coords.lng, coords.lat], zoom: 12 });
    }
  };

  // === REAL-TIME CALCULATION ===
  useEffect(() => {
    const d = parseFloat(distance); const c = parseFloat(consumption);
    if (!d || !c || d <= 0 || c <= 0 || !currentFuelPrice) { setLiveResult(null); return; }
    const effectiveDistance = returnTrip ? d * 2 : d;
    const totalLitres = Math.round((effectiveDistance / 100) * c * 100) / 100;
    const fuelCost = Math.round(totalLitres * currentFuelPrice * 100) / 100;

    // RUC: diesel only, when enabled — use selected weight band rate
    const rucBand = RUC_WEIGHT_BANDS.find(b => b.key === rucTier) || RUC_WEIGHT_BANDS[0];
    const rucCost = (selectedFuelType === "diesel" && rucEnabled) ? calculateRUC(effectiveDistance, rucBand.rate) : 0;

    // Tolls: detected or manual override
    const tollTotal = tollOverride && parseFloat(tollOverride) >= 0
      ? parseFloat(tollOverride)
      : detectedTolls.reduce((sum, t) => sum + t.cost * (returnTrip ? 2 : 1), 0);

    // Ferry
    const ferryTotal = ferryDetected ? (parseFloat(ferryCost) || 0) * (returnTrip ? 2 : 1) : 0;

    const totalCost = Math.round((fuelCost + rucCost + tollTotal + ferryTotal) * 100) / 100;

    setLiveResult({
      distance_km: effectiveDistance, total_litres: totalLitres,
      fuel_cost: fuelCost, ruc_cost: rucCost, toll_cost: tollTotal, ferry_cost: ferryTotal,
      total_cost: totalCost,
    });
    setShowResultAnimation(true); setTimeout(() => setShowResultAnimation(false), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RUC_WEIGHT_BANDS / calculateRUC are module-level; setters are stable
  }, [distance, consumption, currentFuelPrice, returnTrip, selectedFuelType, rucEnabled, rucTier, detectedTolls, tollOverride, ferryDetected, ferryCost]);

  // === FETCH FUEL PRICES ===
  const FALLBACK_PRICES = { diesel: 3.60, unleaded_91: 3.45, unleaded_95: 3.65, source: "fallback" };

  const fetchFuelPrices = useCallback(async () => {
    setLoadingPrice(true);
    try {
      const [pricesResp, regResp] = await Promise.all([
        axios.get(`${API}/gaspy/prices`),
        axios.get(`${API}/gaspy/regional`),
      ]);
      const prices = pricesResp.data;
      // Validate that prices are actual numbers > 0
      if (prices && prices.diesel > 0) {
        setFuelPrices(prices);
        setPriceSource(prices.source || "gaspy.nz");
      } else {
        setFuelPrices(FALLBACK_PRICES);
        setPriceSource("fallback");
      }
      setRegionalPrices(regResp.data.regions || regResp.data);
    } catch (err) {
      console.error("Price fetch error:", err);
      // Use fallback prices if API fails
      if (!fuelPrices || fuelPrices.diesel <= 0) {
        setFuelPrices(FALLBACK_PRICES);
        setPriceSource("fallback");
      }
    }
    finally { setLoadingPrice(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/history`, authConfig());
      setHistory(resp.data);
    } catch (err) { console.warn("Failed to fetch history:", err); } finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => { fetchFuelPrices(); fetchHistory(); }, [fetchFuelPrices, fetchHistory]);

  // Re-fetch history when user changes (login/logout)
  useEffect(() => {
    if (user !== undefined) fetchHistory();
  }, [user, fetchHistory]);

  useEffect(() => {
    const handleVisibility = () => { if (document.visibilityState === "visible") fetchFuelPrices(); };
    document.addEventListener("visibilitychange", handleVisibility);
    const interval = setInterval(fetchFuelPrices, 5 * 60 * 1000);
    return () => { document.removeEventListener("visibilitychange", handleVisibility); clearInterval(interval); };
  }, [fetchFuelPrices]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = () => { setShowFuelDropdown(false); setShowRegionDropdown(false); setShowOriginSuggestions(false); setShowDestSuggestions(false); };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Update prices on region change
  useEffect(() => {
    if (!regionalPrices || !selectedRegion) return;
    const region = regionalPrices.find(r => r.region_key === selectedRegion);
    if (region && !priceOverride) setShowResultAnimation(true);
  }, [selectedRegion, regionalPrices, priceOverride]);

  // === ROUTE DRAWING ===
  const drawRoutes = (routes, selectedIdx, oCoords, dCoords) => {
    const map = mapRef.current;
    if (!map) return;
    // Wait for map style to fully load before adding layers
    if (!map.isStyleLoaded()) {
      map.once('style.load', () => drawRoutes(routes, selectedIdx, oCoords, dCoords));
      return;
    }
    removeRouteLayers(map);
    if (originMarkerRef.current) originMarkerRef.current.remove();
    if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
    // Draw alt routes first (underneath)
    routes.forEach((route, i) => {
      if (i === selectedIdx) return;
      const id = `route-alt-${i}`;
      map.addSource(id, { type: 'geojson', data: { type: 'Feature', geometry: route.geometry } });
      map.addLayer({
        id, type: 'line', source: id,
        paint: { 'line-color': '#999999', 'line-width': 5, 'line-opacity': 0.5, 'line-dasharray': [2, 2] },
        layout: { 'line-join': 'round', 'line-cap': 'round' },
      });
    });
    // Draw main route on top
    map.addSource('route-main', { type: 'geojson', data: { type: 'Feature', geometry: routes[selectedIdx].geometry } });
    map.addLayer({
      id: 'route-main', type: 'line', source: 'route-main',
      paint: { 'line-color': '#E52020', 'line-width': 5, 'line-opacity': 0.85 },
      layout: { 'line-join': 'round', 'line-cap': 'round' },
    });
    // Add markers
    originMarkerRef.current = createMarker('origin').setLngLat([oCoords.lng, oCoords.lat]).addTo(map);
    destinationMarkerRef.current = createMarker('destination').setLngLat([dCoords.lng, dCoords.lat]).addTo(map);
    // Fit bounds
    const bounds = new mapboxgl.LngLatBounds();
    routes[selectedIdx].geometry.coordinates.forEach(c => bounds.extend(c));
    map.fitBounds(bounds, { padding: 50 });
  };

  const switchRoute = (newIdx) => {
    if (!allRoutes || newIdx >= allRoutes.routes.length) return;
    setActiveRouteIndex(newIdx);
    const route = allRoutes.routes[newIdx];
    const { distanceKm, durationText } = formatRouteInfo(route);
    drawRoutes(allRoutes.routes, newIdx, allRoutes.originCoords, allRoutes.destCoords);
    setRouteInfo({ distance: distanceKm, duration: durationText, originAddress: originInput, destinationAddress: destinationInput, stops: waypoints.length });
    setDistance(distanceKm.toString());
    const otherIdx = newIdx === 0 ? 1 : 0;
    if (allRoutes.routes[otherIdx]) {
      const altInfo = formatRouteInfo(allRoutes.routes[otherIdx]);
      setAltRouteInfo({ distanceKm: altInfo.distanceKm, duration: altInfo.durationText, index: otherIdx });
    }
    toast.success(`Switched to ${newIdx === 0 ? 'main' : 'alternative'} route: ${distanceKm} km`);
  };

  // === CALCULATE ROUTE ===
  const calculateRoute = async () => {
    if (!originInput) { toast.error("Please set a start location"); return; }
    if (!destinationInput) { toast.error("Please set a destination"); return; }
    setLoadingRoute(true); setAltRouteInfo(null); setAllRoutes(null); setActiveRouteIndex(0);
    try {
      let oCoords = originCoordsRef.current;
      if (!oCoords) { const geo = await geocodeAddress(originInput); if (!geo) { toast.error("Could not find start location."); setLoadingRoute(false); return; } oCoords = { lat: geo.lat, lng: geo.lng }; originCoordsRef.current = oCoords; }
      let dCoords = destinationCoordsRef.current;
      if (!dCoords) { const geo = await geocodeAddress(destinationInput); if (!geo) { toast.error("Could not find destination."); setLoadingRoute(false); return; } dCoords = { lat: geo.lat, lng: geo.lng }; destinationCoordsRef.current = dCoords; }
      const wpCoords = [];
      for (const wp of waypoints) { const geo = await geocodeAddress(wp); if (geo) wpCoords.push({ lat: geo.lat, lng: geo.lng }); }
      const routes = await calculateOSRMRoute(oCoords, dCoords, wpCoords);
      if (!routes) { toast.error("No driving route found."); setLoadingRoute(false); return; }
      setAllRoutes({ routes, originCoords: oCoords, destCoords: dCoords });
      drawRoutes(routes, 0, oCoords, dCoords);
      const { distanceKm, durationText } = formatRouteInfo(routes[0]);
      setRouteInfo({ distance: distanceKm, duration: durationText, originAddress: originInput, destinationAddress: destinationInput, stops: waypoints.length });
      setDistance(distanceKm.toString());
      // Auto-detect tolls and ferry
      const tolls = detectTolls(routes[0].geometry.coordinates);
      setDetectedTolls(tolls);
      setTollOverride("");
      const ferry = detectFerry(oCoords, dCoords);
      setFerryDetected(ferry);
      if (ferry) setFerryCost(FERRY_DEFAULT_COST.toString());
      // Detect towing warnings on route
      setTowingWarnings(detectTowingWarnings(routes[0].geometry.coordinates));
      if (routes.length > 1) { const altInfo = formatRouteInfo(routes[1]); setAltRouteInfo({ distanceKm: altInfo.distanceKm, duration: altInfo.durationText, index: 1 }); }
      let msg = `Route: ${distanceKm} km \u2022 ${durationText}`;
      if (waypoints.length > 0) msg += ` \u2022 ${waypoints.length} stop${waypoints.length > 1 ? 's' : ''}`;
      if (routes.length > 1) msg += ` \u2022 Alt route available`;
      toast.success(msg);
      setLoadingStations(true);
      try {
        const [sr, fs] = await Promise.all([
          axios.post(`${API}/route-stations`, { route_coords: routes[0].geometry.coordinates, fuel_type: selectedFuelType }),
          axios.post(`${API}/fuel-stations-along-route`, { route_coords: routes[0].geometry.coordinates, fuel_type: selectedFuelType }),
        ]);
        setRouteStations(sr.data);
        setFuelStations(fs.data);
      }
      catch { setRouteStations(null); setFuelStations(null); } finally { setLoadingStations(false); }
    } catch { toast.error("Failed to calculate route."); } finally { setLoadingRoute(false); }
  };

  const clearRoute = () => {
    originCoordsRef.current = null; destinationCoordsRef.current = null;
    setOriginInput(""); setDestinationInput(""); setRouteInfo(null); setRouteStations(null); setFuelStations(null);
    setAltRouteInfo(null); setAllRoutes(null); setActiveRouteIndex(0);
    setSelectingMode("origin"); setWaypoints([]); setWaypointInput(""); setShowWaypointInput(false);
    setDetectedTolls([]); setTollOverride(""); setFerryDetected(false); setTowingWarnings([]);
    if (originMarkerRef.current) originMarkerRef.current.remove();
    if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
    fuelStationMarkersRef.current.forEach(m => m.remove());
    fuelStationMarkersRef.current = [];
    originMarkerRef.current = null; destinationMarkerRef.current = null;
    const map = mapRef.current;
    if (map) {
      removeRouteLayers(map);
    }
  };

  const addWaypoint = (name) => {
    if (waypoints.length >= 8) return;
    setWaypoints([...waypoints, name]); setWaypointInput(""); setShowWaypointInput(false);
    toast.success(`Stop added: ${name}`);
  };
  const removeWaypoint = (index) => { setWaypoints(waypoints.filter((_, i) => i !== index)); };

  // === HISTORY ACTIONS ===
  const handleSaveToHistory = async () => {
    if (!liveResult) return; setSaving(true);
    try {
      await axios.post(`${API}/history`, {
        distance_km: parseFloat(distance), consumption_l_per_100km: parseFloat(consumption),
        diesel_price_per_litre: currentFuelPrice,
        fuel_type: selectedFuelType, region: selectedRegion, return_trip: returnTrip, trip_name: tripName || null,
        origin_name: originInput || null, destination_name: destinationInput || null,
        origin_coords: originCoordsRef.current ? { lat: originCoordsRef.current.lat, lng: originCoordsRef.current.lng } : null,
        destination_coords: destinationCoordsRef.current ? { lat: destinationCoordsRef.current.lat, lng: destinationCoordsRef.current.lng } : null,
        waypoints: waypoints.length > 0 ? waypoints : null,
        vehicle_name: selectedVehicle?.name || null,
        ruc_cost: liveResult.ruc_cost || 0,
        toll_cost: liveResult.toll_cost || 0,
        ferry_cost: liveResult.ferry_cost || 0,
      }, authConfig());
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ["#E52020", "#16A34A", "#DC2626"] });
      toast.success("Saved to history! Trip data recorded anonymously for community stats."); fetchHistory();
      // Record anonymous trip data (fire-and-forget, no user info)
      axios.post(`${API}/analytics/trip`, {
        distance_km: parseFloat(distance), fuel_type: selectedFuelType,
        fuel_price_per_litre: currentFuelPrice, consumption_l_per_100km: parseFloat(consumption),
        total_cost: liveResult.total_cost, fuel_cost: liveResult.fuel_cost,
        ruc_cost: liveResult.ruc_cost || 0, toll_cost: liveResult.toll_cost || 0, ferry_cost: liveResult.ferry_cost || 0,
        ruc_tier: selectedFuelType === "diesel" && rucEnabled ? rucTier : null,
        origin_city: originInput || null, destination_city: destinationInput || null,
        region: selectedRegion, return_trip: returnTrip,
        vehicle_type: selectedVehicle?.vehicle_type || null,
      }).catch(() => {});
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const handleDeleteHistory = async (id) => {
    try { await axios.delete(`${API}/history/${id}`, authConfig()); setHistory(history.filter(h => h.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };
  const handleClearHistory = async () => {
    try { await axios.delete(`${API}/history`, authConfig()); setHistory([]); toast.success("History cleared"); }
    catch { toast.error("Failed to clear"); }
  };

  const handleReopenHistory = async (item) => {
    // Restore calculator inputs
    const dist = item.return_trip ? item.distance_km / 2 : item.distance_km;
    setDistance(dist.toString());
    setConsumption(item.consumption_l_per_100km.toString());
    setSelectedFuelType(item.fuel_type || "diesel");
    setSelectedRegion(item.region || "national");
    setReturnTrip(!!item.return_trip);
    setTripName(item.trip_name || "");
    if (item.diesel_price_per_litre) {
      setPriceOverride(item.diesel_price_per_litre.toString());
      setShowPriceOverride(true);
    }
    // Restore origin/destination
    let oCoords = item.origin_coords;
    let dCoords = item.destination_coords;
    if (item.origin_name) {
      setOriginInput(item.origin_name);
      // Auto-geocode if no coordinates stored
      if (!oCoords) {
        const geo = await geocodeAddress(item.origin_name);
        if (geo) oCoords = { lat: geo.lat, lng: geo.lng };
      }
      if (oCoords) originCoordsRef.current = oCoords;
    }
    if (item.destination_name) {
      setDestinationInput(item.destination_name);
      if (!dCoords) {
        const geo = await geocodeAddress(item.destination_name);
        if (geo) dCoords = { lat: geo.lat, lng: geo.lng };
      }
      if (dCoords) destinationCoordsRef.current = dCoords;
    }
    if (item.waypoints?.length > 0) setWaypoints(item.waypoints);
    // Place markers on map
    if (mapRef.current && oCoords && dCoords) {
      if (originMarkerRef.current) originMarkerRef.current.remove();
      if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
      originMarkerRef.current = createMarker('origin').setLngLat([oCoords.lng, oCoords.lat]).addTo(mapRef.current);
      destinationMarkerRef.current = createMarker('destination').setLngLat([dCoords.lng, dCoords.lat]).addTo(mapRef.current);
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([oCoords.lng, oCoords.lat]);
      bounds.extend([dCoords.lng, dCoords.lat]);
      mapRef.current.fitBounds(bounds, { padding: 50 });
    }
    toast.success(oCoords && dCoords ? "Trip restored — click GET ROUTE to recalculate" : "Trip inputs restored");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // === RENDER ===
  if (showSplash) return (
    <div className="splash-screen" data-testid="splash-screen">
      <img src="/icon-192.png" alt="DRIVE$PEND" className="splash-icon" />
      <h1 className="splash-title">DRIVE<span>$</span>PEND</h1>
      <p className="splash-tagline">PREMIUM TRAVEL COST CALCULATOR</p>
    </div>
  );

  if (showPrivacy) return <PrivacyPolicy onBack={() => setShowPrivacy(false)} />;

  if (showAdmin) return (
    <AdminPage onBack={() => { setShowAdmin(false); window.history.replaceState(null, "", window.location.pathname); }} />
  );

  if (showLogin) return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Toaster position="top-center" richColors />
      <LoginPage onLogin={handleLogin} />
    </div>
  );

  if (user === undefined) return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-full border-2 border-[#EBE6DC] border-t-[#E52020] animate-spin" />
        <div className="text-[#999999] text-xs font-mono tracking-[0.3em] uppercase">Initializing</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F5F0] sf-app" style={pullDistance > 0 ? { transform: `translateY(${pullDistance}px)`, transition: pulling.current ? 'none' : 'transform 0.3s ease' } : {}}>
      <Toaster position="top-center" richColors />

      {/* Pull to Refresh indicator */}
      {pullDistance > 0 && (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[99999] pointer-events-none" style={{ transform: `translateY(${Math.min(pullDistance - 40, 20)}px)` }}>
          <div className={`bg-white shadow-lg rounded-full p-2.5 ${refreshing ? 'animate-spin' : ''}`} style={{ opacity: Math.min(pullDistance / PULL_THRESHOLD, 1) }}>
            <RefreshCw className={`w-5 h-5 ${pullDistance >= PULL_THRESHOLD ? 'text-[#E52020]' : 'text-[#999999]'}`} />
          </div>
        </div>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="bg-[#E52020] text-white px-4 py-3 flex items-center justify-center gap-3 text-sm font-['Manrope']" data-testid="pwa-install-banner">
          {isIOSSafari ? (
            <span className="text-center leading-snug">
              Install DRIVE$PEND: tap <strong style={{fontSize: '16px'}}>&#8593;</strong> (Share) then <strong>"Add to Home Screen"</strong>
            </span>
          ) : (
            <span>Add DRIVE$PEND to your home screen</span>
          )}
          {!isIOSSafari && (
            <button onClick={handleInstall} className="px-3 py-1 bg-white text-[#E52020] rounded-full text-xs font-bold hover:bg-[#F7F5F0] transition-colors" data-testid="pwa-install-btn">INSTALL</button>
          )}
          <button onClick={dismissInstallBanner} className="text-white/70 hover:text-white ml-1 text-lg leading-none" data-testid="pwa-install-dismiss">&times;</button>
        </div>
      )}

      {/* User Bar */}
      <div className="sf-user-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-end gap-3 h-12">
          {user ? (
            <>
              <div className="flex items-center gap-2">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-sm border border-[rgba(229, 32, 32,0.3)]" /> : <User className="w-5 h-5 text-[#E52020]" />}
                <span className="text-sm text-[#1A1A1A] font-medium hidden sm:inline font-['Manrope']">{user.name}</span>
              </div>
              <button onClick={handleLogout} className="text-xs text-[#666666] hover:text-[#DC2626] transition-colors flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-[rgba(220,38,38,0.06)]" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sign out</span>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-xs text-[#999999] hover:text-[#DC2626] transition-colors flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-[rgba(220,38,38,0.06)]"
                data-testid="delete-account-btn"
                title="Permanently delete your account and data"
              >
                <Trash2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Delete</span>
              </button>
            </>
          ) : (
            <button onClick={() => setShowLogin(true)} className="text-sm text-[#E52020] font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.12)] hover:bg-[rgba(229, 32, 32,0.12)] transition-all font-['Manrope']" data-testid="signin-btn">
              <User className="w-3.5 h-3.5" />Sign in
            </button>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal (Apple Guideline 5.1.1(v)) */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[100000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setShowDeleteConfirm(false)}
          data-testid="delete-account-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-[#DC2626]" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-[#1A1A1A]">Delete your account?</h3>
                <p className="text-sm text-[#666666] mt-1">This is permanent and cannot be undone.</p>
              </div>
            </div>
            <div className="bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl p-3 text-xs text-[#666666] space-y-1 mb-5">
              <p className="font-semibold text-[#1A1A1A]">We will immediately delete:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Your account &amp; login session</li>
                <li>All saved commutes, vehicles &amp; work trips</li>
                <li>Your calculation history</li>
                <li>Any POIs you submitted</li>
              </ul>
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-full bg-[#FAFAF7] border border-[#EBE6DC] text-sm font-semibold text-[#1A1A1A] hover:bg-[#F0EDE5] transition-all disabled:opacity-50"
                data-testid="delete-account-cancel-btn"
              >
                Keep my account
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-full bg-[#DC2626] text-white text-sm font-bold hover:bg-[#B91C1C] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="delete-account-confirm-btn"
              >
                {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <header className="text-center mb-10 sm:mb-12 page-enter">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#EBE6DC] mb-5 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.2em] text-[#666666] uppercase">Live · Aotearoa New Zealand</span>
          </div>
          <h1 className="sf-heading text-4xl sm:text-6xl lg:text-7xl mb-4 break-words" data-testid="app-title">DRIVE<span>$</span>PEND</h1>
          <p className="text-[#666666] text-base sm:text-lg max-w-xl mx-auto font-light tracking-tight">Calculate your travel costs with precision — live NZ fuel prices from Gaspy.</p>
        </header>

        <div className="main-grid">
          <div className="page-enter delay-100 mb-6 lg:mb-0">
            <FuelHud
              selectedFuelType={selectedFuelType} setSelectedFuelType={setSelectedFuelType}
              showFuelDropdown={showFuelDropdown} setShowFuelDropdown={setShowFuelDropdown}
              fuelPrices={fuelPrices} loadingPrice={loadingPrice} currentFuelPrice={currentFuelPrice}
              fetchFuelPrices={fetchFuelPrices} priceSource={priceSource}
              priceOverride={priceOverride} setPriceOverride={setPriceOverride}
              showPriceOverride={showPriceOverride} setShowPriceOverride={setShowPriceOverride}
              gaspyFuelPrice={gaspyFuelPrice}
              showRegionDropdown={showRegionDropdown} setShowRegionDropdown={setShowRegionDropdown}
              regionalPrices={regionalPrices} selectedRegion={selectedRegion} setSelectedRegion={setSelectedRegion}
              liveResult={liveResult} showResultAnimation={showResultAnimation}
              rucEnabled={rucEnabled} setRucEnabled={setRucEnabled}
              rucTier={rucTier} setRucTier={setRucTier}
              detectedTolls={detectedTolls} tollOverride={tollOverride} setTollOverride={setTollOverride}
              ferryDetected={ferryDetected} ferryCost={ferryCost} setFerryCost={setFerryCost}
              returnTrip={returnTrip}
              selectedVehicle={selectedVehicle}
            />
            <SplitShare
              liveResult={liveResult} distance={distance} consumption={consumption}
              selectedFuelType={selectedFuelType} currentFuelPrice={currentFuelPrice}
              returnTrip={returnTrip} originInput={originInput} destinationInput={destinationInput}
              tripName={tripName}
            />
            <MileageReimbursement
              liveResult={liveResult}
              selectedFuelType={selectedFuelType}
              selectedVehicle={selectedVehicle}
              returnTrip={returnTrip}
              user={user}
              originInput={originInput}
              destinationInput={destinationInput}
              tripName={tripName}
              onSaved={() => setMileageRefreshKey(k => k + 1)}
            />
            <div className="sf-card">
              <MapWidget
                mapContainerRef={mapContainerRef}
                selectingMode={selectingMode} setSelectingMode={setSelectingMode}
                originInput={originInput} handleOriginSearch={handleOriginSearch}
                originSuggestions={originSuggestions} showOriginSuggestions={showOriginSuggestions} setShowOriginSuggestions={setShowOriginSuggestions}
                selectOriginSuggestion={selectOriginSuggestion} locateMe={locateMe}
                destinationInput={destinationInput} handleDestSearch={handleDestSearch}
                destSuggestions={destSuggestions} showDestSuggestions={showDestSuggestions} setShowDestSuggestions={setShowDestSuggestions}
                selectDestSuggestion={selectDestSuggestion}
                waypoints={waypoints} waypointInput={waypointInput} setWaypointInput={setWaypointInput}
                showWaypointInput={showWaypointInput} setShowWaypointInput={setShowWaypointInput}
                waypointInputRef={waypointInputRef} addWaypoint={addWaypoint} removeWaypoint={removeWaypoint}
                calculateRoute={calculateRoute} clearRoute={clearRoute} loadingRoute={loadingRoute}
                routeInfo={routeInfo} altRouteInfo={altRouteInfo} switchRoute={switchRoute}
                user={user} setShowLogin={setShowLogin} setCommuteFromRoute={setCommuteFromRoute}
                consumption={consumption} selectedFuelType={selectedFuelType} returnTrip={returnTrip}
                poiEnabled={poiEnabled} setPoiEnabled={setPoiEnabled}
                poiTypes={poiTypes} setPoiTypes={setPoiTypes}
                addingCustomPin={addingCustomPin}
                onStartCustomPin={() => { setAddingCustomPin(true); addingCustomPinRef.current = true; toast.info("Click on the map to pin a location"); }}
                onCancelCustomPin={() => { setAddingCustomPin(false); addingCustomPinRef.current = false; }}
                originCoords={originCoordsRef.current}
                destinationCoords={destinationCoordsRef.current}
                fuelStations={fuelStations}
              />
              <RouteStationsPanel routeStations={routeStations} loadingStations={loadingStations} routeInfo={routeInfo} selectedFuelType={selectedFuelType} fuelStations={fuelStations} />
              <CalculatorInputs
                distance={distance} setDistance={setDistance}
                consumption={consumption} handleConsumptionChange={handleConsumptionChange}
                returnTrip={returnTrip} setReturnTrip={setReturnTrip}
                tripName={tripName} setTripName={setTripName}
                saving={saving} loadingPrice={loadingPrice} liveResult={liveResult}
                handleSaveToHistory={handleSaveToHistory} handleShareTrip={handleShareTrip}
                handleOpenShareCard={() => setShowShareCard(true)}
                shareCopied={shareCopied} sharedTrip={sharedTrip} setSharedTrip={setSharedTrip}
                user={user} selectedVehicle={selectedVehicle} setSelectedVehicle={setSelectedVehicle}
              />
            </div>
          </div>

          <div>
            <HistoryPanel history={history} loadingHistory={loadingHistory} handleDeleteHistory={handleDeleteHistory} handleClearHistory={handleClearHistory} handleExportCSV={handleExportCSV} user={user} handleReopenHistory={handleReopenHistory} />
            <div className="mt-6">
              <CommutesPanel user={user} fuelPrices={fuelPrices} regionalPrices={regionalPrices} prefillRoute={commuteFromRoute} onPrefillConsumed={() => setCommuteFromRoute(null)} />
            </div>
            <div className="mt-6 sf-card p-5">
              <button
                onClick={() => setTowingEnabled(!towingEnabled)}
                className={`w-full flex items-center gap-2 text-xs font-['Manrope'] font-bold tracking-wider transition-colors ${towingEnabled ? 'text-[#E52020]' : 'text-[#999999]'}`}
                data-testid="towing-toggle"
              >
                {towingEnabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                <Truck className="w-3.5 h-3.5" />
                TOWING RESTRICTIONS
              </button>
              {towingEnabled && (
                <div className="mt-3 bg-[#FFF7ED] border border-[#FDBA74] rounded-sm p-3 space-y-2" data-testid="towing-info-panel">
                  <div className="text-[10px] font-['Manrope'] font-bold text-[#92400E] tracking-wider">NZ TOWING LIMITS</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#78350F]">
                    <span>Max trailer length:</span><span className="font-mono font-bold">12.5m</span>
                    <span>Max combined length:</span><span className="font-mono font-bold">22m</span>
                    <span>Max open road speed:</span><span className="font-mono font-bold">90 km/h</span>
                    <span>Max height:</span><span className="font-mono font-bold">4.3m</span>
                    <span>Max width:</span><span className="font-mono font-bold">2.55m</span>
                    <span>Full licence max:</span><span className="font-mono font-bold">6,000 kg</span>
                    <span>Learner/Restricted:</span><span className="font-mono font-bold">4,500 kg</span>
                  </div>
                  <p className="text-[9px] text-[#92400E]">Rear overhang &gt;1m requires a fluorescent flag (40x30cm min).</p>
                  {towingWarnings && towingWarnings.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      <div className="flex items-center gap-1 text-[10px] font-['Manrope'] font-bold text-[#DC2626] tracking-wider">
                        <AlertTriangle className="w-3.5 h-3.5" />ROUTE WARNINGS
                      </div>
                      {towingWarnings.map((w, i) => (
                        <div key={`${w.road || "road"}-${w.name || i}`} className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm px-2.5 py-1.5">
                          <div className="text-xs font-bold text-[#991B1B]">{w.name} <span className="font-normal text-[#B91C1C]">({w.road})</span></div>
                          <p className="text-[10px] text-[#B91C1C] mt-0.5">{w.warning}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {towingWarnings && towingWarnings.length === 0 && liveResult && (
                    <div className="flex items-center gap-1.5 mt-1 text-[10px] text-[#16A34A] font-['Manrope'] font-bold">
                      <span>&#10003;</span> No towing restrictions detected on this route
                    </div>
                  )}
                  <button
                    onClick={handleReportRestriction}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-['Manrope'] font-bold tracking-wider text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-sm hover:bg-[#FDE68A] transition-colors"
                    data-testid="report-towing-restriction-btn"
                  >
                    <Flag className="w-3 h-3" />REPORT A TOWING RESTRICTION
                  </button>
                </div>
              )}
            </div>
            <div className="mt-6">
              <TrendsChart />
            </div>
            <div className="mt-6">
              <CommunityStats />
            </div>
          </div>
        </div>

        {/* Mileage reimbursement saved trips — only for logged-in users */}
        <MileageHistoryPanel user={user} refreshKey={mileageRefreshKey} />

        <footer className="text-center mt-12 text-sm text-[#999999] font-mono">
          <p>Prices sourced from{" "}<a href="https://www.gaspy.nz/stats.html" target="_blank" rel="noopener noreferrer" className="text-[#E52020] hover:underline">Gaspy NZ</a></p>
          <div className="flex items-center justify-center gap-4 mt-3">
            <a href="https://www.banjosodyssey.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#999999] hover:text-[#E52020] transition-colors font-['Manrope'] tracking-wider">BANJOS ODYSSEY</a>
            <span className="text-[#D4CEC0]">|</span>
            <a href="https://www.facebook.com/profile.php?id=61565260862671" target="_blank" rel="noopener noreferrer" className="text-xs text-[#999999] hover:text-[#1877F2] transition-colors font-['Manrope'] tracking-wider">FACEBOOK</a>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2 mb-3">
            <button onClick={() => setShowPrivacy(true)} className="text-xs text-[#999999] hover:text-[#E52020] transition-colors font-['Manrope'] tracking-wider" data-testid="privacy-link">PRIVACY POLICY</button>
            <span className="text-[#D4CEC0]">|</span>
            <a href="mailto:admin@drivespend.nz" className="text-xs text-[#999999] hover:text-[#E52020] transition-colors font-['Manrope'] tracking-wider" data-testid="contact-us-link">CONTACT US</a>
            <span className="text-[#D4CEC0]">|</span>
            <button onClick={() => setShowAdmin(true)} className="text-xs text-[#999999] hover:text-[#E52020] transition-colors font-['Manrope'] tracking-wider" data-testid="admin-link">ADMIN</button>
          </div>
          <p className="text-[10px] text-[#999999] font-['Manrope'] tracking-[0.18em] mb-16">
            Questions, feedback or partnerships → <a href="mailto:admin@drivespend.nz" className="text-[#E52020] hover:underline font-medium" data-testid="contact-email">admin@drivespend.nz</a>
          </p>
        </footer>
      </div>

      {/* Share Card Modal */}
      <ShareCard
        open={showShareCard}
        onClose={() => setShowShareCard(false)}
        liveResult={liveResult}
        distance={distance}
        consumption={consumption}
        selectedFuelType={selectedFuelType}
        currentFuelPrice={currentFuelPrice}
        returnTrip={returnTrip}
        tripName={tripName}
        originInput={originInput}
        destinationInput={destinationInput}
        selectedVehicle={selectedVehicle}
      />

      {/* POI Submission Modal */}
      {showPinModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowPinModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="poi-submit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-['Manrope'] font-bold text-[#1A1A1A] tracking-wider">SUBMIT A LOCATION</h3>
              <button onClick={() => setShowPinModal(false)} className="text-[#999999] hover:text-[#1A1A1A]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[10px] text-[#666666] mb-4 font-['Manrope']">Your submission will be reviewed before appearing on the map.</p>
            {pinCoords && (
              <div className="flex gap-2 mb-3">
                <input
                  type="number" step="any" value={pinCoords.lat} onChange={(e) => setPinCoords(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                  className="flex-1 border border-[#EBE6DC] rounded-lg px-3 py-2 text-xs font-mono text-[#1A1A1A] focus:outline-none focus:border-[#E52020]"
                  data-testid="poi-lat-input"
                />
                <input
                  type="number" step="any" value={pinCoords.lng} onChange={(e) => setPinCoords(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                  className="flex-1 border border-[#EBE6DC] rounded-lg px-3 py-2 text-xs font-mono text-[#1A1A1A] focus:outline-none focus:border-[#E52020]"
                  data-testid="poi-lng-input"
                />
              </div>
            )}
            <div className="space-y-3">
              <input
                type="text" value={pinName} onChange={(e) => setPinName(e.target.value)}
                placeholder="Location name"
                className="w-full border border-[#EBE6DC] rounded-lg px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-[#E52020]"
                data-testid="poi-name-input" autoFocus
              />
              <select
                value={pinType} onChange={(e) => setPinType(e.target.value)}
                className="w-full border border-[#EBE6DC] rounded-lg px-3 py-2.5 text-sm text-[#1A1A1A] bg-white focus:outline-none focus:border-[#E52020]"
                data-testid="poi-type-select"
              >
                {PIN_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <textarea
                value={pinDesc} onChange={(e) => setPinDesc(e.target.value)}
                placeholder="Description (optional)"
                rows={3}
                className="w-full border border-[#EBE6DC] rounded-lg px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#999999] focus:outline-none focus:border-[#E52020] resize-none"
                data-testid="poi-desc-input"
              />
              <button
                onClick={handlePinSubmit}
                disabled={!pinName.trim() || pinSubmitting}
                className="w-full bg-[#E52020] hover:bg-[#CC1B1B] disabled:bg-[#D4CEC0] text-white font-['Manrope'] font-bold tracking-wider py-2.5 rounded-lg transition-colors text-sm"
                data-testid="poi-submit-btn"
              >
                {pinSubmitting ? "SUBMITTING..." : "SUBMIT FOR REVIEW"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
